import { useCallback, useEffect, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from '@web-chat-core/ui-kit';
import { useUserSettingsContext } from 'contexts';
import { Wrapper, Track, Thumb, Rail, Fill } from './view';

const MIN_VOLUME = 0;
const MAX_VOLUME = 100;
const IDLE_COMMIT_MS = 500; // мс

/** Ограничивает значение громкости диапазоном [MIN_VOLUME, MAX_VOLUME]. */
const clamp = (v: number): number => Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, v));

/** Перевод value в проценты */
const valueToPercent = (v: number): number => ((v - MIN_VOLUME) / (MAX_VOLUME - MIN_VOLUME)) * 100;

/** Пишем позицию в CSS-переменную. */
const setPos = (el: HTMLElement | null, percent: number): void => {
    el?.style.setProperty('--pos', `${percent}%`);
};

/**
 * VolumeSlider - ползунок громкости. Во время драга позиция пишется напрямую
 * в CSS-переменную --pos (минуя React-ререндер), из-за чего setSpeakerVolume
 * вызывается один раз — на pointerup/pointercancel.
 */
export const VolumeSlider = observer(function VolumeSlider() {
    const { speakerVolume, setSpeakerVolume } = useUserSettingsContext();
    const value = speakerVolume * 100;

    /** DOM-узел трека — нужен для setPointerCapture и getBoundingClientRect. */
    const trackRef = useRef<HTMLDivElement | null>(null);

    /** DOM-узел враппера — на него вешается CSS-переменная --pos. */
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    /** Кеш bounding rect трека на время драга; null вне драга. */
    const rectRef = useRef<DOMRect | null>(null);

    /** Идёт ли сейчас драг. В ref, чтобы старт/конец не вызывали ререндер. */
    const draggingRef = useRef(false);

    /** Id запланированного requestAnimationFrame; null — кадр не запланирован. */
    const rafRef = useRef<number | null>(null);

    /** Последняя clientX от pointer-события — будет применена в ближайшем flush. */
    const pendingXRef = useRef(0);

    /** Последнее значение, посчитанное в драге; коммитится в onChange на pointerup или idle-таймере. */
    const lastValueRef = useRef(value);

    /** Последнее значение, реально отправленное в onChange — для дедупликации коммитов. */
    const lastCommittedRef = useRef(value);

    /** Id idle-таймера: коммит при паузе в драге дольше IDLE_COMMIT_MS. */
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useLayoutEffect(() => {
        if (draggingRef.current) return;
        lastValueRef.current = value;
        lastCommittedRef.current = value;
        setPos(wrapperRef.current, valueToPercent(value));
    }, [value]);

    /** Коммит lastValueRef в setSpeakerVolume с дедупликацией; чистит idle-таймер. */
    const commit = useCallback(() => {
        if (idleTimerRef.current !== null) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        // Защита от кейса, когда пользователь просто держит ползунок на одном значении и не отпускает при этом.
        if (lastValueRef.current === lastCommittedRef.current) return;
        lastCommittedRef.current = lastValueRef.current;
        setSpeakerVolume(Math.round(lastValueRef.current) / 100);
    }, [setSpeakerVolume]);

    /** Вычисляем позицию и отдаём хелперу setPos. И сбрасывает idle-таймер на каждом кадре. */
    const flush = useCallback(() => {
        rafRef.current = null;
        const rect = rectRef.current;
        if (!rect) return;
        const ratio = (pendingXRef.current - rect.left) / rect.width;
        const v = clamp(MIN_VOLUME + ratio * (MAX_VOLUME - MIN_VOLUME));
        lastValueRef.current = v;
        setPos(wrapperRef.current, valueToPercent(v));
        if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(commit, IDLE_COMMIT_MS);
    }, [commit]);

    /** Запоминает координату курсора и планирует один flush на ближайший кадр. */
    const schedule = useCallback(
        (clientX: number): void => {
            pendingXRef.current = clientX;
            if (rafRef.current === null) {
                rafRef.current = requestAnimationFrame(flush);
            }
        },
        [flush],
    );

    /** Старт драга: захватывает указатель, кеширует rect трека и сразу применяет клик. */
    const handlePointerDown = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>): void => {
            const track = trackRef.current;
            if (!track) return;
            // setPointerCapture перенаправляет все дальнейшие pointer-события для этого pointerId
            // на текущий элемент, даже если курсор/палец уйдёт за пределы трека. Без этого
            // pointermove перестанет приходить, как только указатель выедет за границы, и драг
            // оборвётся. Снимется автоматически на pointerup/pointercancel.
            e.currentTarget.setPointerCapture(e.pointerId);
            rectRef.current = track.getBoundingClientRect();
            draggingRef.current = true;
            schedule(e.clientX);
        },
        [schedule],
    );

    /** Планирует обновление во время драга. */
    const handlePointerMove = useCallback(
        (e: ReactPointerEvent<HTMLDivElement>): void => {
            if (!draggingRef.current) return;
            schedule(e.clientX);
        },
        [schedule],
    );

    /** Завершение/отмена драга: коммитит финальное значение через commit (с дедупом и сбросом idle-таймера). */
    const handlePointerUp = useCallback((): void => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        rectRef.current = null;
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        commit();
    }, [commit]);

    useEffect(
        () => () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
            rafRef.current = null;
            idleTimerRef.current = null;
            rectRef.current = null;
            draggingRef.current = false;
            pendingXRef.current = 0;
            trackRef.current = null;
            wrapperRef.current = null;
        },
        [],
    );

    return (
        <Wrapper ref={wrapperRef}>
            <Icon icon="icSpeaker20" size={20} colorVariant="basic" />
            <Track
                ref={trackRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                // onPointerCancel срабатывает, когда браузер принудительно отнимает указатель
                // в середине жеста — типично для тач-устройств. После cancel pointerup
                // уже НЕ придёт. Без этого хендлера состояние драга останется "застывшим"
                // (draggingRef = true, rect закеширован), и при следующем pointermove
                // ползунок продолжит реагировать без явного pointerdown.
                onPointerCancel={handlePointerUp}
                role="slider"
                aria-valuemin={MIN_VOLUME}
                aria-valuemax={MAX_VOLUME}
                aria-valuenow={value}
            >
                <Rail />
                <Fill />
                <Thumb />
            </Track>
            <Icon icon="icVolume20" size={20} colorVariant="basic" />
        </Wrapper>
    );
});
